import { useState } from 'react';
import { patientsAPI } from '../lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Pencil, Loader2 } from 'lucide-react';

const TITLES = ['Mr', 'Mrs', 'Miss', 'Master', 'Dr', 'Prof'];
const GENDERS = ['male', 'female', 'other'];
const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

function calcAgeFromDOB(dob) {
  if (!dob) return '';
  const d = new Date(dob);
  if (isNaN(d.getTime())) return '';
  const today = new Date();
  let age = today.getFullYear() - d.getFullYear();
  const m = today.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--;
  return age >= 0 && age < 150 ? String(age) : '';
}
function dobFromAge(age) {
  const a = parseInt(age, 10);
  if (isNaN(a) || a < 0 || a > 130) return '';
  const today = new Date();
  return new Date(today.getFullYear() - a, today.getMonth(), today.getDate()).toISOString().slice(0, 10);
}

/**
 * Inline "Edit Patient" modal. Reused by Patient Detail page so Front Desk / Billing / Admin
 * can fix mistakes (typo in name, wrong phone, missing address) on the fly.
 */
export default function EditPatientDialog({ patient, open, onOpenChange, onSaved }) {
  const [form, setForm] = useState(() => ({
    title: patient.title || '',
    first_name: patient.first_name || '',
    last_name: patient.last_name || '',
    date_of_birth: patient.date_of_birth || '',
    age: patient.date_of_birth ? calcAgeFromDOB(patient.date_of_birth) : '',
    gender: patient.gender || '',
    blood_group: patient.blood_group || '',
    marital_status: patient.marital_status || '',
    occupation: patient.occupation || '',
    phone: (patient.phone || '').slice(0, 10),
    alternate_phone: (patient.alternate_phone || '').slice(0, 10),
    email: patient.email || '',
    address: patient.address || '',
    city: patient.city || '',
    state: patient.state || '',
    pincode: patient.pincode || '',
    emergency_contact_name: patient.emergency_contact_name || '',
    emergency_contact_phone: (patient.emergency_contact_phone || '').slice(0, 10),
    emergency_contact_relation: patient.emergency_contact_relation || '',
    allergies: (patient.allergies || []).join(', '),
    chronic_conditions: (patient.chronic_conditions || []).join(', '),
  }));
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const onChangeDOB = (v) => setForm(f => ({ ...f, date_of_birth: v, age: calcAgeFromDOB(v) }));
  const onChangeAge = (v) => {
    const cleaned = v.replace(/\D/g, '');
    setForm(f => ({ ...f, age: cleaned, date_of_birth: dobFromAge(cleaned) }));
  };
  const onPhone = (k) => (e) => set(k, e.target.value.replace(/\D/g, '').slice(0, 10));

  const handleSave = async () => {
    if (!form.first_name || !form.last_name || !form.phone) {
      toast.error('First name, last name and phone are required');
      return;
    }
    if (form.phone.length !== 10) {
      toast.error('Phone number must be exactly 10 digits');
      return;
    }
    if (form.emergency_contact_phone && form.emergency_contact_phone.length !== 10) {
      toast.error('Emergency phone must be exactly 10 digits');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        allergies: form.allergies ? form.allergies.split(',').map(s => s.trim()).filter(Boolean) : [],
        chronic_conditions: form.chronic_conditions ? form.chronic_conditions.split(',').map(s => s.trim()).filter(Boolean) : [],
      };
      delete payload.age;
      await patientsAPI.update(patient.id, payload);
      toast.success('Patient details updated');
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="edit-patient-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil size={16} className="text-emerald-600" /> Edit Patient — {patient.patient_id}
          </DialogTitle>
          <p className="text-xs text-slate-500">Fix typos or missing details. Identifier (UHID) stays the same.</p>
        </DialogHeader>

        <div className="space-y-5">
          <section>
            <h4 className="text-sm font-semibold text-slate-700 mb-2.5">Personal Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Title</Label>
                <Select value={form.title} onValueChange={v => set('title', v)}>
                  <SelectTrigger data-testid="edit-title"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{TITLES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div />
              <div>
                <Label>First Name *</Label>
                <Input value={form.first_name} onChange={e => set('first_name', e.target.value)} required data-testid="edit-first-name" />
              </div>
              <div>
                <Label>Last Name *</Label>
                <Input value={form.last_name} onChange={e => set('last_name', e.target.value)} required data-testid="edit-last-name" />
              </div>
              <div>
                <Label>Date of Birth</Label>
                <Input type="date" value={form.date_of_birth} onChange={e => onChangeDOB(e.target.value)} data-testid="edit-dob" />
              </div>
              <div>
                <Label>Age <span className="text-[10px] text-slate-400">(auto)</span></Label>
                <Input value={form.age} onChange={e => onChangeAge(e.target.value)} inputMode="numeric" maxLength={3} data-testid="edit-age" />
              </div>
              <div>
                <Label>Gender</Label>
                <Select value={form.gender} onValueChange={v => set('gender', v)}>
                  <SelectTrigger data-testid="edit-gender"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{GENDERS.map(g => <SelectItem key={g} value={g}>{g[0].toUpperCase() + g.slice(1)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Blood Group</Label>
                <Select value={form.blood_group} onValueChange={v => set('blood_group', v)}>
                  <SelectTrigger data-testid="edit-blood"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{BLOOD_GROUPS.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Marital Status</Label>
                <Select value={form.marital_status} onValueChange={v => set('marital_status', v)}>
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    {['single', 'married', 'divorced', 'widowed'].map(s => <SelectItem key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Occupation</Label>
                <Input value={form.occupation} onChange={e => set('occupation', e.target.value)} />
              </div>
            </div>
          </section>

          <section>
            <h4 className="text-sm font-semibold text-slate-700 mb-2.5">Contact</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Phone * <span className="text-[10px] text-slate-400">(10 digits)</span></Label>
                <Input value={form.phone} onChange={onPhone('phone')} required inputMode="numeric" maxLength={10} data-testid="edit-phone" />
              </div>
              <div>
                <Label>Alternate Phone</Label>
                <Input value={form.alternate_phone} onChange={onPhone('alternate_phone')} inputMode="numeric" maxLength={10} />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={e => set('email', e.target.value)} data-testid="edit-email" />
              </div>
            </div>
          </section>

          <section>
            <h4 className="text-sm font-semibold text-slate-700 mb-2.5">Address</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <Label>Address</Label>
                <Textarea rows={2} value={form.address} onChange={e => set('address', e.target.value)} data-testid="edit-address" />
              </div>
              <div><Label>City</Label><Input value={form.city} onChange={e => set('city', e.target.value)} /></div>
              <div><Label>State</Label><Input value={form.state} onChange={e => set('state', e.target.value)} /></div>
              <div><Label>Pincode</Label><Input value={form.pincode} onChange={e => set('pincode', e.target.value.replace(/\D/g, '').slice(0, 6))} inputMode="numeric" maxLength={6} /></div>
            </div>
          </section>

          <section>
            <h4 className="text-sm font-semibold text-slate-700 mb-2.5">Emergency Contact</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div><Label>Name</Label><Input value={form.emergency_contact_name} onChange={e => set('emergency_contact_name', e.target.value)} /></div>
              <div><Label>Phone</Label><Input value={form.emergency_contact_phone} onChange={onPhone('emergency_contact_phone')} inputMode="numeric" maxLength={10} /></div>
              <div><Label>Relation</Label><Input value={form.emergency_contact_relation} onChange={e => set('emergency_contact_relation', e.target.value)} /></div>
            </div>
          </section>

          <section>
            <h4 className="text-sm font-semibold text-slate-700 mb-2.5">Medical Snapshot</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Allergies <span className="text-[10px] text-slate-400">(comma separated)</span></Label>
                <Input value={form.allergies} onChange={e => set('allergies', e.target.value)} />
              </div>
              <div>
                <Label>Chronic Conditions <span className="text-[10px] text-slate-400">(comma separated)</span></Label>
                <Input value={form.chronic_conditions} onChange={e => set('chronic_conditions', e.target.value)} />
              </div>
            </div>
          </section>
        </div>

        <DialogFooter className="gap-2 pt-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="edit-save-btn">
            {saving ? <><Loader2 size={14} className="mr-1 animate-spin" /> Saving...</> : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
